import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { Zoom } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Icon from '../../icons/Icon';
import { ICONS } from '../../icons';

import './CommonModal.scss';

const CommonModal = ({
  isOpen,
  onClose,
  children,
  wrapperClassName,
  hideCloseIcon,
  isAnimated,
  animationTimeout = 250,
  autoClose,
}) => {
  const { t } = useTranslation();
  const modalWrapperRef = useRef(null);

  const timerSpanRef = useRef(null);
  const intervalRef = useRef(null);

  const handleOutsideClick = useCallback(
    (e) => {
      if (modalWrapperRef.current.contains(e.target)) return;

      const clickedOverlay = e.target.closest('.common-modal_overlay');
      if (clickedOverlay && !clickedOverlay.contains(modalWrapperRef.current)) {
        return;
      }

      onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!window.onscroll) {
      window.onscroll = () => {
        document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
      };
    }
    return () => (window.onscroll = null);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }

    return () => {
      if (isOpen) {
        document.body.style.overflowY = 'auto';
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (isOpen && autoClose) {
      const initialRemaining = Math.ceil(autoClose / 1000);
      if (timerSpanRef.current) timerSpanRef.current.textContent = String(initialRemaining);

      intervalRef.current = window.setInterval(() => {
        const remaining = Number(timerSpanRef.current.textContent) - 1;

        if (timerSpanRef.current) {
          timerSpanRef.current.textContent = String(remaining);
        }

        if (remaining <= 0) {
          if (intervalRef.current != null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          onClose();
        }
      }, 1000);
    }

    if (!isOpen && autoClose && timerSpanRef.current) {
      timerSpanRef.current.textContent = '';
    }

    return () => {
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, autoClose, onClose]);

  const portalElement = ReactDOM.createPortal(
    <div className='common-modal_overlay' onClick={handleOutsideClick} role='presentation'>
      <Zoom in={Boolean(isOpen)} timeout={isAnimated ? animationTimeout : 0}>
        <div ref={modalWrapperRef} className={clsx('common-modal_wrapper', wrapperClassName)} role='presentation'>
          {!hideCloseIcon ? (
            <div className='common-modal_close-block'>
              <Icon
                onClick={onClose}
                icon={ICONS.Cross}
                disableCommonStyles
                disableCommonColors
                className='common-modal_close-icon'
              />
            </div>
          ) : null}

          {children}

          {autoClose && (
            <div className='common-modal_auto-close'>
              {t('thisModalWillCloseIn')}
              <span className='common-modal_auto-close_seconds' role='timer' ref={timerSpanRef} />
              {t('seconds')}
            </div>
          )}
        </div>
      </Zoom>
    </div>,
    document.getElementById('root'),
  );

  return isOpen ? portalElement : null;
};

export default CommonModal;
